import type { TutorialStep } from "./types.ts";

/**
 * 基本チュートリアル「プロジェクトの作成とタスクの作成・編集」のステップ定義。
 *
 * 流れ:
 *   プロジェクト作成 → タスク作成 → 詳細パネルで修正(優先度) →
 *   ステータス展開で選択肢を確認 → チェックボックスで完了 →
 *   完了タスクの確認(フィルター・完了者・完了日)
 *
 * targetKey は SandboxScreen 側の data-tut 属性と対応する。
 */
export const BASIC_TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: "welcome",
    targetKey: null,
    placement: "center",
    title: "チュートリアルへようこそ",
    body:
      "これは練習用のサンドボックス画面です。ここでの操作は実際のデータには保存されません。安心して試してください。\n" +
      "プロジェクトの作成から、タスクの作成・編集・完了確認までを体験します。",
  },
  {
    id: "add-project",
    targetKey: "project-add",
    placement: "right",
    title: "① プロジェクトを作成する",
    body: "まずは左サイドバーの「＋」ボタンを押して、プロジェクトの作成画面を開きましょう。",
    expectAction: "open-list-dialog",
  },
  {
    id: "create-project",
    targetKey: "list-dialog",
    placement: "right",
    title: "プロジェクト名を入力",
    body: "プロジェクト名を入力し、「プロジェクトを作成」ボタンを押してください。",
    expectAction: "create-project",
  },
  {
    id: "project-created",
    targetKey: "active-project",
    placement: "right",
    title: "プロジェクトができました",
    body: "作成したプロジェクトがサイドバーに追加され、選択された状態になりました。このプロジェクトにタスクを追加していきます。",
  },
  {
    id: "add-task",
    targetKey: "fab",
    placement: "left",
    title: "② タスクを作成する",
    body: "画面右下の「＋」ボタンを押して、タスクの作成画面を開きましょう。",
    expectAction: "open-task-dialog",
  },
  {
    id: "create-task",
    targetKey: "task-dialog",
    placement: "right",
    title: "タスクを入力",
    body:
      "タスク名を入力して「作成」を押してください。\n" +
      "重要度などの項目もここで設定できます。",
    expectAction: "create-task",
  },
  {
    id: "select-task",
    targetKey: "task-row",
    placement: "bottom",
    title: "③ 詳細を開いて修正する",
    body: "作成したタスクの行をクリックすると、右側に詳細パネルが開きます。クリックしてみましょう。",
    expectAction: "select-task",
  },
  {
    id: "detail-overview",
    targetKey: "detail-panel",
    placement: "left",
    title: "詳細パネルでできること",
    body:
      "右側の詳細パネルが開きました。\n" +
      "ここではステータス・期限・担当者・重要度・タグ・カスタム項目・メモなど、タスクのあらゆる情報を確認・編集できます。",
  },
  {
    id: "change-priority",
    targetKey: "detail-priority",
    placement: "left",
    title: "重要度を変更してみましょう",
    body:
      "試しに重要度を変更してみましょう。\n" +
      "変更内容はすぐに反映されます。",
    expectAction: "change-priority",
  },
  {
    id: "expand-status",
    targetKey: "task-status",
    placement: "bottom",
    title: "④ ステータスを変更する",
    body:
      "タスクには現在のステータスが表示されています。\n" +
      "クリックして展開すると「未対応・対応中・完了・非表示」などから選んで変更できます。試しに展開してみましょう。",
    expectAction: "open-status-menu",
  },
  {
    id: "complete-by-checkbox",
    targetKey: "task-checkbox",
    placement: "bottom",
    title: "チェックボックスで完了にする",
    body:
      "ステータスメニューからでも完了にできますが、左のチェックボックスをクリックすると、ワンタップでタスクを完了にできます。\n" +
      "チェックボックスをクリックして、タスクを完了にしましょう。",
    expectAction: "complete-task",
  },
  {
    id: "completed-hidden",
    targetKey: "show-completed",
    placement: "right",
    title: "完了タスクの確認",
    body:
      "完了したタスクは通常の一覧から見えなくなります。\n" +
      "「完了タスクを表示」をオンにして、完了済みのタスクを表示しましょう。",
    expectAction: "toggle-show-completed",
  },
  {
    id: "show-completer",
    targetKey: "task-row",
    placement: "bottom",
    title: "完了者・完了日を確認",
    body: "完了したタスクには、誰がいつ完了したか（完了者・完了日）が表示されます。後から振り返るときに役立ちます。",
  },
  {
    id: "done",
    targetKey: null,
    placement: "center",
    title: "お疲れさまでした！",
    body:
      "基本操作の体験は以上です。実際の画面でも同じ手順で操作できます。\n" +
      "「完了」を押すとチュートリアルを終了します。",
  },
];
